import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useProjects, useUsers, useEpics } from '../../context/AppContext';
import {
    Loader2, User as UserIcon, Shield, Briefcase, Trash2, Plus, X,
    Search, Users, Crown, UserCog, Eye, ChevronDown, Pencil,
    Building2, Clock, CheckCircle2, AlertCircle, LucideIcon, Settings, Check, MapPin, Link,
    FileSpreadsheet, Download, Upload, BarChart3, TrendingUp, Activity, Calendar
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { User, Project } from '../../types';
import { ConfirmModal } from '../modals/ConfirmModal';
import { ImportEpicsModal } from '../modals/ImportEpicsModal';
import { exportEpics } from '../../utils/excelImport';

// Define Assignment type locally if not available in types.ts (common pattern if backend specific)
interface Assignment {
    id: string;
    userId: string;
    projectId: string;
}

interface KPICardProps {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    sublabel?: string;
    color: string;
    delay?: number;
}

// KPI Card Component
function KPICard({ icon, label, value, sublabel, color, delay = 0 }: KPICardProps) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay }}
            className="group relative overflow-hidden rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider p-4 transition-all duration-200 hover:shadow-siemens hover:border-siemens-petrol/30"
        >
            <div className={cn("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", color)} />
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", color.replace('from-', 'bg-').split(' ')[0] + '/10')}>
                {icon}
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-siemens-deep-blue dark:text-white tabular-nums">{value}</span>
                {sublabel && (
                    <span className="text-xs font-medium text-siemens-text-soft">{sublabel}</span>
                )}
            </div>
            <div className="mt-1">
                <span className="text-sm font-semibold text-siemens-deep-blue-80 dark:text-siemens-text-soft">{label}</span>
            </div>
        </motion.div>
    );
}

// Role Badge Component
interface RoleBadgeProps {
    role: string; // or specific UserRole enum
}

function RoleBadge({ role }: RoleBadgeProps) {
    const config: Record<string, { label: string; color: string; icon: LucideIcon }> = {
        admin: { label: 'Admin', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: Crown },
        pm: { label: 'Project Manager', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', icon: UserCog },
        executive: { label: 'Executive', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: Eye },
        owner: { label: 'Epic Owner', color: 'bg-green-500/10 text-green-600 dark:text-green-400', icon: Briefcase },
        normal: { label: 'User', color: 'bg-slate-500/10 text-slate-600 dark:text-slate-400', icon: UserIcon },
    };
    const { label, color, icon: Icon } = config[role] || config.normal;
    return (
        <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold", color)}>
            <Icon size={12} />
            {label}
        </span>
    );
}

// User Avatar Component
interface UserAvatarProps {
    user: User;
    size?: 'sm' | 'md' | 'lg';
}

function UserAvatar({ user, size = 'md' }: UserAvatarProps) {
    const sizes = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
    };
    const initials = user.displayName
        ? user.displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
        : user.email?.charAt(0).toUpperCase() || 'U';

    return (
        <div className={cn(
            "rounded-full bg-gradient-to-br from-siemens-petrol to-siemens-petrol-dark flex items-center justify-center text-white font-bold",
            sizes[size]
        )}>
            {initials}
        </div>
    );
}

// Format relative time
function formatRelativeTime(dateString?: string | null) {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface NewUserForm {
    email: string;
    displayName: string;
    role: string;
    projectIds: string[];
}

interface Notification {
    type: 'success' | 'error';
    message: string;
}

// Analytics Tab Component
interface AnalyticsTabProps {
    api: ReturnType<typeof useApi>;
}

function AnalyticsTab({ api }: AnalyticsTabProps) {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [days, setDays] = useState(7);
    const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
    const [allUsersData, setAllUsersData] = useState<any>(null); // Store unfiltered data for user list

    useEffect(() => {
        loadAnalytics();
    }, [days, selectedUserEmail]);

    // Load all users data separately (unfiltered) for the user list
    useEffect(() => {
        const loadAllUsers = async () => {
            try {
                const summary = await api.getAnalyticsSummary(days);
                setAllUsersData(summary);
            } catch {
                // Silently fail for user list
            }
        };
        if (selectedUserEmail) {
            loadAllUsers();
        }
    }, [days, selectedUserEmail, api]);

    const loadAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const summary = await api.getAnalyticsSummary(days, selectedUserEmail || undefined);
            setData(summary);
            if (!selectedUserEmail) {
                setAllUsersData(summary);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (userEmail: string) => {
        if (selectedUserEmail === userEmail) {
            setSelectedUserEmail(null); // Toggle off if clicking same user
        } else {
            setSelectedUserEmail(userEmail);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 className="w-8 h-8 text-siemens-petrol animate-spin" />
                <p className="text-sm text-siemens-text-soft">Loading analytics...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <AlertCircle className="w-12 h-12 text-siemens-text-soft opacity-50" />
                <p className="text-sm text-siemens-text-soft">{error || 'No data available'}</p>
                <button
                    onClick={loadAnalytics}
                    className="btn-siemens-secondary text-sm"
                >
                    Retry
                </button>
            </div>
        );
    }

    const { summary, pageViewDistribution, topUsers, dailyActivity } = data;

    // Calculate max for chart scaling
    const maxPageViews = Math.max(...Object.values(pageViewDistribution || {}).map(v => v as number), 1);
    const maxDailyUsers = Math.max(...(dailyActivity || []).map((d: any) => d.activeUsers), 1);

    // Page name formatting
    const formatPageName = (page: string) => {
        const names: Record<string, string> = {
            'kanban': 'Kanban',
            'roadmap': 'Roadmap',
            'matrix': 'Matrix',
            'executive': 'Executive',
            'dashboard': 'Dashboard',
            'projects': 'Projects',
            'list': 'List',
            'admin-users': 'Settings',
            'admin-audit': 'Audit',
        };
        return names[page] || page.charAt(0).toUpperCase() + page.slice(1);
    };

    return (
        <div className="space-y-6 mt-4">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-siemens-deep-blue dark:text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-siemens-petrol" />
                    Usage Analytics
                </h2>
                <div className="flex items-center gap-2">
                    {[7, 14, 30].map(d => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={cn(
                                "px-3 py-1.5 text-xs font-semibold rounded-lg transition-all",
                                days === d
                                    ? "bg-siemens-petrol text-white"
                                    : "bg-siemens-deep-blue-8/50 dark:bg-siemens-elevation-selected text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                            )}
                        >
                            {d} days
                        </button>
                    ))}
                </div>
            </div>

            {/* Active Filter Indicator */}
            {selectedUserEmail && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-siemens-petrol/10 border border-siemens-petrol/20 rounded-lg px-4 py-2 flex items-center justify-between"
                >
                    <span className="text-sm text-siemens-petrol font-medium">
                        Filtering by: {(allUsersData?.topUsers || []).find((u: any) => u.email === selectedUserEmail)?.displayName || selectedUserEmail}
                    </span>
                    <button
                        onClick={() => setSelectedUserEmail(null)}
                        className="text-xs text-siemens-petrol hover:text-siemens-petrol-dark font-bold flex items-center gap-1"
                    >
                        <X size={14} />
                        Clear filter
                    </button>
                </motion.div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard
                    icon={<Users className="w-5 h-5 text-siemens-petrol" />}
                    label="Active Users"
                    value={summary.activeUsers}
                    sublabel={`last ${days} days`}
                    color="from-siemens-petrol to-siemens-petrol-light"
                    delay={0}
                />
                <KPICard
                    icon={<Eye className="w-5 h-5 text-blue-500" />}
                    label="Page Views"
                    value={summary.totalPageViews}
                    color="from-blue-500 to-blue-400"
                    delay={0.1}
                />
                <KPICard
                    icon={<Crown className="w-5 h-5 text-amber-500" />}
                    label="Most Active"
                    value={summary.mostActiveUser?.displayName || summary.mostActiveUser?.email?.split('@')[0] || 'N/A'}
                    sublabel={summary.mostActiveUser ? `${summary.mostActiveUser.count} events` : ''}
                    color="from-amber-500 to-amber-400"
                    delay={0.2}
                />
                <KPICard
                    icon={<TrendingUp className="w-5 h-5 text-green-500" />}
                    label="Popular View"
                    value={summary.mostPopularPage ? formatPageName(summary.mostPopularPage.page) : 'N/A'}
                    sublabel={summary.mostPopularPage ? `${summary.mostPopularPage.views} views` : ''}
                    color="from-green-500 to-green-400"
                    delay={0.3}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Page View Distribution */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider"
                >
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-white mb-4 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-siemens-petrol" />
                        Page Views by View
                    </h3>
                    {Object.keys(pageViewDistribution || {}).length === 0 ? (
                        <div className="py-8 text-center">
                            <BarChart3 className="w-10 h-10 text-siemens-text-soft mx-auto mb-3 opacity-50" />
                            <p className="text-sm text-siemens-text-soft">No page views recorded yet</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {Object.entries(pageViewDistribution)
                                .sort((a, b) => (b[1] as number) - (a[1] as number))
                                .slice(0, 8)
                                .map(([page, count]) => (
                                    <div key={page} className="flex items-center gap-3">
                                        <span className="text-xs font-medium text-siemens-text-soft w-20 truncate">
                                            {formatPageName(page)}
                                        </span>
                                        <div className="flex-1 h-6 bg-siemens-deep-blue-8/30 dark:bg-black/20 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${((count as number) / maxPageViews) * 100}%` }}
                                                transition={{ duration: 0.5, delay: 0.1 }}
                                                className="h-full bg-gradient-to-r from-siemens-petrol to-siemens-petrol-light rounded-full flex items-center justify-end pr-2"
                                            >
                                                <span className="text-[10px] font-bold text-white">
                                                    {count as number}
                                                </span>
                                            </motion.div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </motion.div>

                {/* Daily Activity Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider"
                >
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-white mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-siemens-petrol" />
                        Daily Active Users
                    </h3>
                    {(dailyActivity || []).length === 0 ? (
                        <div className="py-8 text-center">
                            <Calendar className="w-10 h-10 text-siemens-text-soft mx-auto mb-3 opacity-50" />
                            <p className="text-sm text-siemens-text-soft">No activity data yet</p>
                        </div>
                    ) : (
                        <div className="flex items-end gap-1 h-32">
                            {(dailyActivity || []).slice(-14).map((day: any, idx: number) => (
                                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${(day.activeUsers / maxDailyUsers) * 100}%` }}
                                        transition={{ duration: 0.3, delay: idx * 0.02 }}
                                        className="w-full bg-gradient-to-t from-siemens-petrol to-siemens-petrol-light rounded-t min-h-[4px]"
                                        title={`${day.date}: ${day.activeUsers} users, ${day.pageViews} views`}
                                    />
                                    <span className="text-[8px] text-siemens-text-soft">
                                        {new Date(day.date).toLocaleDateString('en', { day: 'numeric' })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Top Users Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl border border-siemens-deep-blue/8 dark:border-siemens-divider overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-siemens-deep-blue/8 dark:border-siemens-divider">
                    <h3 className="text-sm font-bold text-siemens-deep-blue dark:text-white flex items-center gap-2">
                        <UserCog className="w-4 h-4 text-siemens-petrol" />
                        Top Users by Activity
                    </h3>
                </div>
                {(topUsers || []).length === 0 ? (
                    <div className="py-12 text-center">
                        <Users className="w-10 h-10 text-siemens-text-soft mx-auto mb-3 opacity-50" />
                        <p className="text-sm text-siemens-text-soft">No user activity recorded yet</p>
                    </div>
                ) : (
                    <table className="siemens-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Events</th>
                                <th>Last Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {((allUsersData || data)?.topUsers || []).slice(0, 10).map((user: any, idx: number) => (
                                <tr
                                    key={user.email || user.userId}
                                    onClick={() => handleUserClick(user.email)}
                                    className={cn(
                                        "cursor-pointer transition-all",
                                        selectedUserEmail === user.email
                                            ? "bg-siemens-petrol/15 dark:bg-siemens-petrol/20 ring-2 ring-siemens-petrol ring-inset"
                                            : "hover:bg-siemens-deep-blue-8/30 dark:hover:bg-siemens-elevation-selected/50"
                                    )}
                                >
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold",
                                                selectedUserEmail === user.email
                                                    ? "bg-siemens-petrol ring-2 ring-offset-2 ring-siemens-petrol"
                                                    : "bg-gradient-to-br from-siemens-petrol to-siemens-petrol-dark"
                                            )}>
                                                {idx + 1}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-siemens-deep-blue dark:text-white">
                                                    {user.displayName || user.email?.split('@')[0] || 'Unknown'}
                                                </span>
                                                {user.email && user.displayName && (
                                                    <span className="text-xs text-siemens-text-soft">{user.email}</span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="px-2 py-1 rounded-full bg-siemens-petrol/10 text-siemens-petrol text-xs font-bold">
                                            {user.count}
                                        </span>
                                    </td>
                                    <td className="text-siemens-text-soft text-xs">
                                        {formatRelativeTime(user.lastActive)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </motion.div>
        </div>
    );
}

interface SettingsPageProps {
    onAddEpic?: () => void;
    onExport?: () => void;
    onViewHistory?: () => void;
    onSaveSession?: () => void;
    onCompare?: () => void;
    onToggleSettings?: () => void;
}

export default function SettingsPage({
    onAddEpic,
    onExport,
    onViewHistory,
    onSaveSession,
    onCompare,
    onToggleSettings
}: SettingsPageProps) {
    const api = useApi();
    const { isAdmin } = useAuth();
    const { projects } = useProjects();
    const { users, setUsers } = useUsers();
    const { epics } = useEpics();

    const [assignments, setAssignments] = useState<Assignment[]>([]);

    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [newUserForm, setNewUserForm] = useState<NewUserForm>({ email: '', displayName: '', role: 'normal', projectIds: [] });

    const [editUserForm, setEditUserForm] = useState({ id: '', email: '', displayName: '', role: 'normal' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Confirmation States
    const [confirmRemoveAssignment, setConfirmRemoveAssignment] = useState<string | null>(null);
    const [confirmDeleteUser, setConfirmDeleteUser] = useState<User | null>(null);
    const [accessForm, setAccessForm] = useState<Record<string, boolean>>({
        projects: false,
        list: false,
        kanban: false,
        roadmap: false,
        matrix: false,
        dashboard: false,
        executive: false,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [activeTab, setActiveTab] = useState<'users' | 'actions' | 'analytics'>('actions');
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [notification, setNotification] = useState<Notification | null>(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Analytics state
    const [analyticsData, setAnalyticsData] = useState<any>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsDays, setAnalyticsDays] = useState(7);

    useEffect(() => {
        if (isAdmin) {
            loadData();
        }
    }, [isAdmin]);

    // Auto-dismiss notifications
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersData, assignmentsData] = await Promise.all([
                api.getUsers(),
                api.getAssignments()
            ]);
            setUsers(usersData || []);
            setAssignments(assignmentsData || []);
        } catch (error) {
            console.error('Failed to load users:', error);
            showNotification('error', 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (type: 'success' | 'error', message: string) => {
        setNotification({ type, message });
    };

    const handleRoleChange = async (userId: string, newRole: string) => {
        setActionLoading(userId);
        try {
            await api.updateUser(userId, { role: newRole });
            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
            showNotification('success', 'Role updated successfully');
        } catch (error) {
            showNotification('error', 'Failed to update role');
        } finally {
            setActionLoading(null);
        }
    };

    const handleAssignProject = async (projectId: string) => {
        if (!selectedUser) return;
        setActionLoading('assign');
        try {
            const newAssignment = await api.createAssignment({ userId: selectedUser.id, projectId });
            setAssignments([...assignments, newAssignment]);
            showNotification('success', 'Project assigned');
        } catch (error) {
            showNotification('error', 'Failed to assign project');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRemoveAssignmentClick = (assignmentId: string) => {
        setConfirmRemoveAssignment(assignmentId);
    };

    const handleConfirmRemoveAssignment = async () => {
        if (!confirmRemoveAssignment) return;
        try {
            await api.deleteAssignment(confirmRemoveAssignment);
            setAssignments(assignments.filter(a => a.id !== confirmRemoveAssignment));
            showNotification('success', 'Assignment removed');
            setConfirmRemoveAssignment(null);
        } catch (error) {
            showNotification('error', 'Failed to remove assignment');
        }
    };

    const handleOpenAccessModal = (user: User) => {
        setSelectedUser(user);
        setAccessForm(user.customPageAccess || {
            projects: false,
            list: false,
            kanban: false,
            roadmap: false,
            matrix: false,
            dashboard: false,
            executive: false,
        });
        setIsAccessModalOpen(true);
    };

    const handleSaveAccess = async () => {
        if (!selectedUser) return;
        setActionLoading('access');
        try {
            await api.updateUser(selectedUser.id, { customPageAccess: accessForm });
            setUsers(users.map(u => u.id === selectedUser.id ? { ...u, customPageAccess: accessForm } : u));
            setIsAccessModalOpen(false);
            showNotification('success', 'Page access updated');
        } catch (error) {
            showNotification('error', 'Failed to update page access');
        } finally {
            setActionLoading(null);
        }
    };

    const handleAddUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newUserForm.email) return;
        setActionLoading('addUser');
        try {
            const added = await api.createUser(newUserForm);
            setUsers([...users, added]);
            setIsAddUserModalOpen(false);
            setNewUserForm({ email: '', displayName: '', role: 'normal', projectIds: [] });
            showNotification('success', 'User created successfully');
            if (newUserForm.projectIds.length > 0) loadData();
        } catch (error) {
            showNotification('error', 'Failed to create user. It may already exist.');
            console.error(error);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteUserClick = (user: User) => {
        setConfirmDeleteUser(user);
    };

    const handleConfirmDeleteUser = async () => {
        if (!confirmDeleteUser) return;
        setActionLoading(confirmDeleteUser.id);
        setConfirmDeleteUser(null);
        try {
            await api.deleteUser(confirmDeleteUser.id);
            setUsers(users.filter(u => u.id !== confirmDeleteUser.id));
            showNotification('success', 'User deleted');
            loadData();
        } catch (error) {
            showNotification('error', 'Failed to delete user');
            console.error(error);
        } finally {
            setActionLoading(null);
        }
    };

    const handleEditUserClick = (user: User) => {
        setEditUserForm({
            id: user.id,
            email: user.email,
            displayName: user.displayName || '',
            role: user.role || 'normal'
        });
        setIsEditUserModalOpen(true);
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoading('editUser');
        try {
            await api.updateUser(editUserForm.id, {
                email: editUserForm.email,
                displayName: editUserForm.displayName,
                role: editUserForm.role
            });
            setUsers(users.map(u => u.id === editUserForm.id ? {
                ...u,
                email: editUserForm.email,
                displayName: editUserForm.displayName,
                role: editUserForm.role
            } : u));
            setIsEditUserModalOpen(false);
            showNotification('success', 'User updated successfully');
        } catch (error) {
            showNotification('error', 'Failed to update user');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCleanupUnnamedUsers = async () => {
        if (!confirm('This will delete all users with generic names (Unnamed User, User) who have never logged in. Continue?')) {
            return;
        }
        setActionLoading('cleanup');
        try {
            const result = await api.cleanupUnnamedUsers();
            showNotification('success', result.message);
            loadData(); // Refresh user list
        } catch (error) {
            showNotification('error', 'Failed to cleanup users');
            console.error(error);
        } finally {
            setActionLoading(null);
        }
    };

    // Stats calculation
    const stats = useMemo(() => {
        const roleCounts: Record<string, number> = { admin: 0, pm: 0, executive: 0, owner: 0, normal: 0 };
        users.forEach(u => {
            const role = u.role || 'normal';
            roleCounts[role] = (roleCounts[role] || 0) + 1;
        });
        const recentLogins = users.filter(u => {
            if (!u.lastLoginAt) return false;
            const diff = Date.now() - new Date(u.lastLoginAt).getTime();
            return diff < 7 * 24 * 60 * 60 * 1000; // 7 days
        }).length;
        return { total: users.length, roleCounts, recentLogins };
    }, [users]);

    // Filtered users
    const filteredUsers = useMemo(() => {
        const filtered = users.filter(user => {
            const matchesSearch = !searchQuery ||
                user.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesRole = roleFilter === 'all' || user.role === roleFilter;
            return matchesSearch && matchesRole;
        });

        return filtered;
    }, [users, searchQuery, roleFilter]);

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, roleFilter]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Helper functions
    const getUserAssignments = (userId: string) => {
        return assignments.filter(a => a.userId === userId).map(a => {
            const project = projects.find(p => p.id === a.projectId);
            return {
                id: a.id,
                projectId: a.projectId,
                projectName: project ? project.name : 'Unknown Project'
            };
        });
    };

    const getUnassignedProjects = (userId: string) => {
        const assignedIds = assignments.filter(a => a.userId === userId).map(a => a.projectId);
        return projects.filter(p => !assignedIds.includes(p.id));
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-96 gap-4">
                <Loader2 className="w-8 h-8 text-siemens-petrol animate-spin" />
                <p className="text-sm text-siemens-text-soft">Loading users...</p>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto">
            <div className="p-6 max-w-7xl mx-auto space-y-6">
                {/* Notification Toast */}
                <AnimatePresence>
                    {notification && (
                        <motion.div
                            initial={{ opacity: 0, y: -20, x: '-50%' }}
                            animate={{ opacity: 1, y: 0, x: '-50%' }}
                            exit={{ opacity: 0, y: -20, x: '-50%' }}
                            className={cn(
                                "fixed top-4 left-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2",
                                notification.type === 'success'
                                    ? "bg-green-500 text-white"
                                    : "bg-red-500 text-white"
                            )}
                        >
                            {notification.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            <span className="text-sm font-medium">{notification.message}</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-siemens-deep-blue dark:text-white flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-siemens-petrol/10 flex items-center justify-center">
                                <Settings className="w-5 h-5 text-siemens-petrol" />
                            </div>
                            Settings & Administration
                        </h1>
                        <p className="text-sm text-siemens-text-soft mt-1">Manage global settings, users, and roles</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-siemens-deep-blue/10 dark:border-siemens-divider">
                    <button
                        onClick={() => setActiveTab('actions')}
                        className={cn(
                            "px-6 py-3 text-sm font-bold border-b-2 transition-colors",
                            activeTab === 'actions'
                                ? "border-siemens-petrol text-siemens-petrol"
                                : "border-transparent text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                        )}
                    >
                        Global Actions
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        className={cn(
                            "px-6 py-3 text-sm font-bold border-b-2 transition-colors",
                            activeTab === 'users'
                                ? "border-siemens-petrol text-siemens-petrol"
                                : "border-transparent text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                        )}
                    >
                        User Management
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={cn(
                            "px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2",
                            activeTab === 'analytics'
                                ? "border-siemens-petrol text-siemens-petrol"
                                : "border-transparent text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                        )}
                    >
                        <BarChart3 size={16} />
                        Analytics
                    </button>
                </div>

                {activeTab === 'actions' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Action Cards */}
                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-siemens-petrol/10 text-siemens-petrol">
                                    <Plus className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">New Content</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Start a new epic to track strategic initiatives.</p>
                            <button
                                onClick={onAddEpic}
                                className="w-full py-2 bg-siemens-petrol text-white rounded-lg font-bold hover:bg-siemens-petrol-dark transition-colors"
                            >
                                Add Epic
                            </button>
                        </div>

                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Snapshots</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Save current state as a historical snapshot.</p>
                            <button
                                onClick={onSaveSession}
                                className="w-full py-2 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-colors"
                            >
                                Save Snapshot
                            </button>
                        </div>

                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                                    <AlertCircle className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Comparisons</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Compare current status against previous snapshots.</p>
                            <button
                                onClick={onCompare}
                                className="w-full py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-colors"
                            >
                                Compare Sessions
                            </button>
                        </div>

                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">History & Logs</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">View audit logs and session history.</p>
                            <button
                                onClick={onViewHistory}
                                className="w-full py-2 bg-siemens-feedback-blue text-white rounded-lg font-bold hover:bg-blue-600 transition-colors"
                            >
                                View History
                            </button>
                        </div>

                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                                    <Link className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Export Data</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Download current data as JSON.</p>
                            <button
                                onClick={onExport}
                                className="w-full py-2 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-colors"
                            >
                                Export JSON
                            </button>
                        </div>

                        {/* Import Epics */}
                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                                    <Upload className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Import Epics</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Bulk create or update epics from Excel.</p>
                            <button
                                onClick={() => setIsImportModalOpen(true)}
                                className="w-full py-2 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 transition-colors"
                            >
                                Import Excel
                            </button>
                        </div>

                        {/* Export Epics (Excel) */}
                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                                    <FileSpreadsheet className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Export Epics</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Download all epics as Excel for bulk editing.</p>
                            <button
                                onClick={() => exportEpics(epics, projects)}
                                className="w-full py-2 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition-colors"
                            >
                                Export Excel
                            </button>
                        </div>

                        {/* Preferences */}
                        <div className="bg-white dark:bg-siemens-elevation-base-1 rounded-xl p-6 border border-siemens-deep-blue/8 dark:border-siemens-divider shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                    <Settings className="w-5 h-5" />
                                </div>
                                <h3 className="font-bold text-siemens-deep-blue dark:text-white">Preferences</h3>
                            </div>
                            <p className="text-sm text-siemens-text-soft mb-6 h-10">Toggle theme and app preferences.</p>
                            <button
                                onClick={onToggleSettings}
                                className="w-full py-2 bg-slate-500 text-white rounded-lg font-bold hover:bg-slate-600 transition-colors"
                            >
                                Toggle Theme
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'users' && (
                    <>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4">
                            <div>
                                <h2 className="text-lg font-bold text-siemens-deep-blue dark:text-white">User Accounts</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleCleanupUnnamedUsers}
                                    disabled={actionLoading === 'cleanup'}
                                    className="btn-siemens-secondary flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                    {actionLoading === 'cleanup' ? (
                                        <Loader2 size={18} className="animate-spin" />
                                    ) : (
                                        <Trash2 size={18} />
                                    )}
                                    Cleanup Unnamed
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setIsAddUserModalOpen(true)}
                                    className="btn-siemens-primary flex items-center gap-2"
                                >
                                    <Plus size={18} /> Add User
                                </motion.button>
                            </div>
                        </div>

                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard
                                icon={<Users className="w-5 h-5 text-siemens-petrol" />}
                                label="Total Users"
                                value={stats.total}
                                color="from-siemens-petrol to-siemens-petrol-light"
                                delay={0}
                            />
                            <KPICard
                                icon={<Crown className="w-5 h-5 text-red-500" />}
                                label="Admins"
                                value={stats.roleCounts.admin}
                                sublabel={`${stats.total > 0 ? ((stats.roleCounts.admin / stats.total) * 100).toFixed(0) : 0}%`}
                                color="from-red-500 to-red-400"
                                delay={0.1}
                            />
                            <KPICard
                                icon={<UserCog className="w-5 h-5 text-blue-500" />}
                                label="Project Managers"
                                value={stats.roleCounts.pm}
                                color="from-blue-500 to-blue-400"
                                delay={0.2}
                            />
                            <KPICard
                                icon={<Clock className="w-5 h-5 text-green-500" />}
                                label="Active (7 days)"
                                value={stats.recentLogins}
                                sublabel={`${stats.total > 0 ? ((stats.recentLogins / stats.total) * 100).toFixed(0) : 0}%`}
                                color="from-green-500 to-green-400"
                                delay={0.3}
                            />
                        </div>

                        {/* Search & Filter */}
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="siemens-input pl-10"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                    className="siemens-input pr-10 appearance-none cursor-pointer min-w-[180px]"
                                >
                                    <option value="all">All Roles</option>
                                    <option value="admin">Admins</option>
                                    <option value="pm">Project Managers</option>
                                    <option value="executive">Executives</option>
                                    <option value="owner">Epic Owners</option>
                                    <option value="normal">Users</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft pointer-events-none" />
                            </div>
                        </div>

                        {/* Users Table */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                            className="rounded-xl bg-white dark:bg-siemens-elevation-base-1 border border-siemens-deep-blue/8 dark:border-siemens-divider overflow-hidden"
                        >
                            {filteredUsers.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Users className="w-12 h-12 text-siemens-text-soft mx-auto mb-4 opacity-50" />
                                    <h3 className="text-lg font-semibold text-siemens-deep-blue dark:text-white mb-2">No users found</h3>
                                    <p className="text-sm text-siemens-text-soft">
                                        {searchQuery || roleFilter !== 'all'
                                            ? 'Try adjusting your search or filters'
                                            : 'Add your first user to get started'}
                                    </p>
                                </div>
                            ) : (
                                <table className="siemens-table">
                                    <thead>
                                        <tr>
                                            <th className="w-[35%]">User</th>
                                            <th className="w-[15%]">Role</th>
                                            <th className="w-[30%]">Assigned Projects</th>
                                            <th className="w-[12%]">Last Login</th>
                                            <th className="w-[8%]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>

                                        <AnimatePresence>
                                            {paginatedUsers.map((user, idx) => (
                                                <motion.tr
                                                    key={user.id}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, x: -20 }}
                                                    transition={{ delay: idx * 0.03 }}
                                                    className={cn(
                                                        "hover:bg-siemens-deep-blue-8/50 dark:hover:bg-siemens-elevation-selected/50 transition-colors",
                                                        actionLoading === user.id && "opacity-50"
                                                    )}
                                                >
                                                    <td>
                                                        <div className="flex items-center gap-3">
                                                            <UserAvatar user={user} />
                                                            <div>
                                                                <div className="font-semibold text-siemens-deep-blue dark:text-white">
                                                                    {user.displayName || 'Unnamed User'}
                                                                </div>
                                                                <div className="text-xs text-siemens-text-soft">{user.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={cn(
                                                            "px-2.5 py-1 rounded-full text-xs font-semibold border",
                                                            user.role === 'admin' && "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
                                                            user.role === 'pm' && "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800",
                                                            user.role === 'executive' && "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800",
                                                            user.role === 'owner' && "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800",
                                                            (user.role === 'normal' || !user.role) && "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                                                        )}>
                                                            {user.role === 'pm' ? 'Project Manager' :
                                                                user.role === 'owner' ? 'Epic Owner' :
                                                                    (user.role?.charAt(0).toUpperCase() + user.role?.slice(1)) || 'User'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {getUserAssignments(user.id).slice(0, 3).map(assign => (
                                                                <span
                                                                    key={assign.id}
                                                                    className="inline-flex items-center gap-1 bg-siemens-petrol/10 text-siemens-petrol dark:text-siemens-petrol-light px-2 py-0.5 rounded-md text-xs font-medium"
                                                                >
                                                                    <Building2 size={10} />
                                                                    {assign.projectName}
                                                                    <button
                                                                        onClick={() => handleRemoveAssignmentClick(assign.id)}
                                                                        className="hover:text-red-500 transition-colors ml-0.5"
                                                                    >
                                                                        <X size={12} />
                                                                    </button>
                                                                </span>
                                                            ))}
                                                            {getUserAssignments(user.id).length > 3 && (
                                                                <span className="text-xs text-siemens-text-soft">
                                                                    +{getUserAssignments(user.id).length - 3} more
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => { setSelectedUser(user); setIsAssignmentModalOpen(true); }}
                                                                className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-md text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                                            >
                                                                <Plus size={12} /> Assign
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-sm text-siemens-text-soft" title={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}>
                                                            {formatRelativeTime(user.lastLoginAt)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => handleEditUserClick(user)}
                                                                className="p-2 rounded-lg text-slate-400 hover:text-siemens-petrol hover:bg-siemens-petrol/10 transition-colors"
                                                                title="Edit User"
                                                            >
                                                                <Pencil size={16} />
                                                            </button>
                                                            {user.role === 'normal' && (
                                                                <button
                                                                    onClick={() => handleOpenAccessModal(user)}
                                                                    className="p-2 rounded-lg text-slate-400 hover:text-siemens-petrol hover:bg-siemens-petrol/10 transition-colors relative"
                                                                    title={`Configure Access${user.customPageAccess ? ` (${Object.values(user.customPageAccess).filter(Boolean).length}/7)` : ''}`}
                                                                >
                                                                    <Settings size={16} />
                                                                    {user.customPageAccess && Object.values(user.customPageAccess).filter(Boolean).length > 0 && (
                                                                        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-siemens-petrol rounded-full" />
                                                                    )}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDeleteUserClick(user)}
                                                                disabled={actionLoading === user.id}
                                                                className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all disabled:opacity-50"
                                                                title="Delete User"
                                                            >
                                                                {actionLoading === user.id ? (
                                                                    <Loader2 size={16} className="animate-spin" />
                                                                ) : (
                                                                    <Trash2 size={16} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </AnimatePresence>
                                    </tbody>
                                </table>
                            )}
                        </motion.div>

                        {/* Pagination Controls */}
                        {/* Pagination Controls */}
                        {filteredUsers.length > 0 && (
                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50/50 dark:bg-slate-800/50 rounded-lg mt-4">
                                <div className="text-sm text-siemens-text-soft">
                                    Showing <span className="font-semibold text-siemens-deep-blue dark:text-white">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold text-siemens-deep-blue dark:text-white">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> of <span className="font-semibold text-siemens-deep-blue dark:text-white">{filteredUsers.length}</span> users
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pNum = i + 1;
                                                if (totalPages > 5 && currentPage > 3) {
                                                    pNum = currentPage - 2 + i;
                                                }
                                                if (pNum > totalPages) return null;

                                                return (
                                                    <button
                                                        key={pNum}
                                                        onClick={() => setCurrentPage(pNum)}
                                                        className={cn(
                                                            "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                                                            currentPage === pNum
                                                                ? "bg-siemens-petrol text-white"
                                                                : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                                        )}
                                                    >
                                                        {pNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'analytics' && (
                    <AnalyticsTab api={api} />
                )}

                {/* Add User Modal */}
                <AnimatePresence>
                    {isAddUserModalOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            onClick={() => setIsAddUserModalOpen(false)}
                        >
                            <motion.form
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                onSubmit={handleAddUser}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
                            >
                                {/* Modal Header */}
                                <div className="p-5 bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                            <Plus className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-white">Add New User</h3>
                                            <p className="text-sm text-white/70">Create a new user account</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="p-5 space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            required
                                            value={newUserForm.email}
                                            onChange={e => setNewUserForm({ ...newUserForm, email: e.target.value })}
                                            className="siemens-input"
                                            placeholder="user@siemens.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Display Name (Customer Name)
                                        </label>
                                        <input
                                            type="text"
                                            value={newUserForm.displayName}
                                            onChange={e => setNewUserForm({ ...newUserForm, displayName: e.target.value })}
                                            className="siemens-input"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Role
                                        </label>
                                        <select
                                            value={newUserForm.role}
                                            onChange={e => setNewUserForm({ ...newUserForm, role: e.target.value })}
                                            className="siemens-input"
                                        >
                                            <option value="normal">User</option>
                                            <option value="pm">Project Manager</option>
                                            <option value="executive">Executive</option>
                                            <option value="owner">Epic Owner</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Modal Footer */}
                                <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsAddUserModalOpen(false)}
                                        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={actionLoading === 'addUser'}
                                        className="btn-siemens-primary flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {actionLoading === 'addUser' ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <Plus size={16} />
                                                Create User
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.form>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Assignment Modal */}
                <AnimatePresence>
                    {isAssignmentModalOpen && selectedUser && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            onClick={() => setIsAssignmentModalOpen(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
                            >
                                {/* Modal Header */}
                                <div className="p-5 bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue">
                                    <div className="flex items-center gap-3">
                                        <UserAvatar user={selectedUser} size="lg" />
                                        <div>
                                            <h3 className="text-lg font-bold text-white">{selectedUser.displayName}</h3>
                                            <p className="text-sm text-white/70">Assign projects</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="p-4 max-h-80 overflow-y-auto">
                                    {getUnassignedProjects(selectedUser.id).length === 0 ? (
                                        <div className="py-8 text-center">
                                            <Building2 className="w-10 h-10 text-siemens-text-soft mx-auto mb-3 opacity-50" />
                                            <p className="text-sm text-siemens-text-soft">All projects are already assigned</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            {getUnassignedProjects(selectedUser.id).map(p => (
                                                <button
                                                    key={p.id}
                                                    onClick={() => handleAssignProject(p.id)}
                                                    disabled={actionLoading === 'assign'}
                                                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-siemens-petrol/5 dark:hover:bg-slate-700 transition-colors flex justify-between items-center group"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Building2 className="w-4 h-4 text-siemens-text-soft" />
                                                        <span className="text-sm font-medium text-siemens-deep-blue dark:text-white">
                                                            {p.name}
                                                        </span>
                                                    </div>
                                                    <Plus className="w-4 h-4 text-siemens-petrol opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Modal Footer */}
                                <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900 flex justify-end">
                                    <button
                                        onClick={() => setIsAssignmentModalOpen(false)}
                                        className="btn-siemens-secondary"
                                    >
                                        Done
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Page Access Configuration Modal */}
                <AnimatePresence>
                    {isAccessModalOpen && selectedUser && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            onClick={() => setIsAccessModalOpen(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
                            >
                                {/* Modal Header */}
                                <div className="p-5 bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                            <Settings className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-white">Configure Page Access</h3>
                                            <p className="text-sm text-white/70">{selectedUser.displayName || selectedUser.email}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="p-5 space-y-3">
                                    <p className="text-sm text-siemens-text-soft mb-4">
                                        Select which pages this user can access:
                                    </p>
                                    {[
                                        { key: 'projects', label: 'Projects', description: 'View and manage projects' },
                                        { key: 'list', label: 'List View', description: 'Epic list with sorting' },
                                        { key: 'kanban', label: 'Kanban View', description: 'Drag-and-drop lane management' },
                                        { key: 'roadmap', label: 'Roadmap View', description: 'Timeline visualization' },
                                        { key: 'matrix', label: 'Matrix View', description: 'Priority scatter plot' },
                                        { key: 'dashboard', label: 'Dashboard', description: 'Analytics and charts' },
                                        { key: 'executive', label: 'Executive View', description: 'High-level summary' },
                                    ].map(({ key, label, description }) => (
                                        <label
                                            key={key}
                                            className={cn(
                                                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                                                accessForm[key]
                                                    ? "border-siemens-petrol bg-siemens-petrol/5"
                                                    : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
                                            )}
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded flex items-center justify-center transition-colors",
                                                accessForm[key]
                                                    ? "bg-siemens-petrol text-white"
                                                    : "bg-slate-100 dark:bg-slate-700"
                                            )}>
                                                {accessForm[key] && <Check size={14} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={accessForm[key] || false}
                                                onChange={(e) => setAccessForm({ ...accessForm, [key]: e.target.checked })}
                                                className="sr-only"
                                            />
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold text-siemens-deep-blue dark:text-white">
                                                    {label}
                                                </div>
                                                <div className="text-xs text-siemens-text-soft">
                                                    {description}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                {/* Modal Footer */}
                                <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                                    <button
                                        type="button"
                                        onClick={() => setAccessForm({
                                            projects: true,
                                            list: true,
                                            kanban: true,
                                            roadmap: true,
                                            matrix: true,
                                            dashboard: true,
                                            executive: true,
                                        })}
                                        className="text-sm text-siemens-petrol hover:underline"
                                    >
                                        Select All
                                    </button>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsAccessModalOpen(false)}
                                            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveAccess}
                                            disabled={actionLoading === 'access'}
                                            className="btn-siemens-primary flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {actionLoading === 'access' ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <Check size={16} />
                                                    Save Access
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Edit User Modal */}
                <AnimatePresence>
                    {isEditUserModalOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            onClick={() => setIsEditUserModalOpen(false)}
                        >
                            <motion.form
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                onSubmit={handleUpdateUser}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden"
                            >
                                <div className="p-5 bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                            <Pencil className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-white">Edit User</h3>
                                            <p className="text-sm text-white/70">Update user details</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            required
                                            value={editUserForm.email}
                                            onChange={e => setEditUserForm({ ...editUserForm, email: e.target.value })}
                                            className="siemens-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Display Name (Customer Name)
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={editUserForm.displayName}
                                            onChange={e => setEditUserForm({ ...editUserForm, displayName: e.target.value })}
                                            className="siemens-input"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-siemens-deep-blue dark:text-white mb-1.5">
                                            Role
                                        </label>
                                        <select
                                            value={editUserForm.role}
                                            onChange={e => setEditUserForm({ ...editUserForm, role: e.target.value })}
                                            className="siemens-input"
                                        >
                                            <option value="normal">User</option>
                                            <option value="pm">Project Manager</option>
                                            <option value="executive">Executive</option>
                                            <option value="owner">Epic Owner</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditUserModalOpen(false)}
                                        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={actionLoading === 'editUser'}
                                        className="btn-siemens-primary flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {actionLoading === 'editUser' ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Check size={16} />
                                                Save Changes
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.form>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Confirm Removal Modal */}
                <ConfirmModal
                    isOpen={!!confirmRemoveAssignment}
                    onClose={() => setConfirmRemoveAssignment(null)}
                    onConfirm={handleConfirmRemoveAssignment}
                    title="Remove Assignment?"
                    message="This user will lose access to this project. This action cannot be undone."
                    variant="warning"
                    confirmLabel="Remove"
                />

                {/* Confirm Delete User Modal */}
                <ConfirmModal
                    isOpen={!!confirmDeleteUser}
                    onClose={() => setConfirmDeleteUser(null)}
                    onConfirm={handleConfirmDeleteUser}
                    title="Delete User?"
                    message={
                        <span>
                            Are you sure you want to delete <span className="font-bold">{confirmDeleteUser?.email}</span>?
                            <br /><br />
                            This will check assignments and permanently remove the user and their role settings.
                        </span>
                    }
                    variant="danger"
                    confirmLabel="Delete User"
                />

                {/* Import Epics Modal */}
                <ImportEpicsModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                    onComplete={() => loadData()}
                />
            </div>
        </div>
    );
}
